import { useState, FormEvent } from 'react'
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Props {
  clientSecret: string
  onSuccess: () => void
  onCancel: () => void
}

/**
 * Stripe PaymentElement form.
 * Wrapped inside <Elements> by InvoicePayButton.
 */
export default function PaymentForm({ onSuccess, onCancel }: Props) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setLoading(true)
    setError(null)

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // No redirect — we handle success in-page
        return_url: window.location.href,
      },
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(stripeError.message ?? t('payment.error', 'Erreur de paiement'))
      setLoading(false)
    } else {
      setLoading(false)
      onSuccess()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />

      {error && (
        <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
        >
          {t('common.cancel', 'Annuler')}
        </button>
        <button
          type="submit"
          disabled={loading || !stripe}
          className="flex-1 px-4 py-2.5 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 size={15} className="animate-spin" />}
          {t('payment.confirm', 'Confirmer le paiement')}
        </button>
      </div>
    </form>
  )
}
